/**
 * taxonomy.ts
 *
 * ExpertiseTaxonomy — versioned, signed category definitions for the
 * Global User Registry expertise classification system.
 *
 * Every evidence record, score, and classifier output MUST carry both
 * `taxonomyVersion` and `categoryId` so that historical data remains
 * interpretable after taxonomy changes.
 *
 * Taxonomy changes are propagated via TAXONOMY_VERSION_PUBLISHED events
 * and applied deterministically through the migration engine.
 */

// ---------------------------------------------------------------------------
// Category definition
// ---------------------------------------------------------------------------

export interface TaxonomyCategory {
  readonly id: string;
  readonly label: string;
  readonly parentId?: string;  // for hierarchical taxonomies (future)
  readonly aliases: readonly string[];  // previous IDs — used for migration mapping
  /** Emoji badge icon (MVP) — replaceable with SVG icon reference later */
  readonly badgeIcon: string;
  readonly status: 'active' | 'deprecated';
}

// ---------------------------------------------------------------------------
// Taxonomy migration
// ---------------------------------------------------------------------------

/**
 * Explicit mapping applied when projecting evidence from one taxonomy
 * version to another. Historical evidence is NEVER silently reclassified —
 * remapping is always through a documented `categoryRemap` entry.
 */
export interface TaxonomyMigration {
  readonly fromVersion: string;           // semver
  readonly toVersion: string;             // semver
  /** Maps old categoryId → new categoryId */
  readonly categoryRemap: Readonly<Record<string, string>>;
  readonly effectiveAt: string;           // ISO-8601
}

// ---------------------------------------------------------------------------
// Taxonomy record
// ---------------------------------------------------------------------------

export interface ExpertiseTaxonomy {
  readonly taxonomyId: string;
  /** Semver string e.g. '1.0.0' */
  readonly version: string;
  readonly categories: readonly TaxonomyCategory[];
  /** Explicit migration paths from previous versions */
  readonly migrations: readonly TaxonomyMigration[];
  /** principalId of the owner who approved this taxonomy version */
  readonly approvedBy: string;
  readonly signature: string;
  readonly publishedAt: string;  // ISO-8601
}

// ---------------------------------------------------------------------------
// Seed taxonomy v1.0.0
// ---------------------------------------------------------------------------

export const SEED_TAXONOMY_V1: ExpertiseTaxonomy = {
  taxonomyId: 'mesh-expertise-taxonomy',
  version: '1.0.0',
  categories: [
    {
      id: 'finance',
      label: 'Finance',
      aliases: [],
      badgeIcon: '💰',
      status: 'active',
    },
    {
      id: 'tech',
      label: 'Technology',
      aliases: [],
      badgeIcon: '⚙️',
      status: 'active',
    },
    {
      id: 'design',
      label: 'Design',
      aliases: [],
      badgeIcon: '🎨',
      status: 'active',
    },
    {
      id: 'marketing',
      label: 'Marketing',
      aliases: [],
      badgeIcon: '📣',
      status: 'active',
    },
    {
      id: 'sales',
      label: 'Sales',
      aliases: [],
      badgeIcon: '🤝',
      status: 'active',
    },
    {
      id: 'legal',
      label: 'Legal',
      aliases: [],
      badgeIcon: '⚖️',
      status: 'active',
    },
    {
      id: 'hr',
      label: 'People & HR',
      aliases: [],
      badgeIcon: '👥',
      status: 'active',
    },
    {
      id: 'product',
      label: 'Product',
      aliases: [],
      badgeIcon: '🧩',
      status: 'active',
    },
    {
      id: 'operations',
      label: 'Operations',
      aliases: [],
      badgeIcon: '🏗️',
      status: 'active',
    },
    {
      id: 'support',
      label: 'Customer Support',
      aliases: [],
      badgeIcon: '🛎️',
      status: 'active',
    },
  ],
  migrations: [],
  // These will be set at runtime by the workspace owner
  approvedBy: '',
  signature: '',
  publishedAt: '',
} as const;

// ---------------------------------------------------------------------------
// Taxonomy store
// ---------------------------------------------------------------------------

export interface TaxonomyStore {
  /** Returns the current active taxonomy */
  getCurrent(): ExpertiseTaxonomy;
  /** Returns a specific version */
  getVersion(version: string): ExpertiseTaxonomy | undefined;
  /** Registers a new taxonomy version (called on TAXONOMY_VERSION_PUBLISHED) */
  publish(taxonomy: ExpertiseTaxonomy): void;
  /** Lists all known taxonomy versions ordered by semver */
  listVersions(): string[];
}

export class InMemoryTaxonomyStore implements TaxonomyStore {
  private readonly versions = new Map<string, ExpertiseTaxonomy>();
  private currentVersion: string;

  constructor(seed: ExpertiseTaxonomy = SEED_TAXONOMY_V1) {
    // For initial use before owner signs the taxonomy, we use the seed with runtime values
    const runtimeSeed: ExpertiseTaxonomy = {
      ...seed,
      approvedBy: seed.approvedBy || 'system',
      publishedAt: seed.publishedAt || new Date().toISOString(),
    };
    this.versions.set(runtimeSeed.version, runtimeSeed);
    this.currentVersion = runtimeSeed.version;
  }

  getCurrent(): ExpertiseTaxonomy {
    return this.versions.get(this.currentVersion)!;
  }

  getVersion(version: string): ExpertiseTaxonomy | undefined {
    return this.versions.get(version);
  }

  publish(taxonomy: ExpertiseTaxonomy): void {
    this.versions.set(taxonomy.version, Object.freeze({ ...taxonomy }));
    // Only advance currentVersion if this is newer by semver
    if (isNewerVersion(taxonomy.version, this.currentVersion)) {
      this.currentVersion = taxonomy.version;
    }
  }

  listVersions(): string[] {
    return Array.from(this.versions.keys()).sort(compareSemver);
  }
}

// ---------------------------------------------------------------------------
// Migration engine
// ---------------------------------------------------------------------------

/**
 * Resolves the categoryId of an evidence item when projecting it
 * under a target taxonomy version.
 *
 * If no migration path exists for the transition, the original categoryId
 * is returned unchanged — this is intentional (old scores remain
 * interpretable under their original taxonomy).
 */
export function resolveCategoryId(opts: {
  originalCategoryId: string;
  originalTaxonomyVersion: string;
  targetTaxonomyVersion: string;
  taxonomyStore: TaxonomyStore;
}): string {
  const { originalCategoryId, originalTaxonomyVersion, targetTaxonomyVersion, taxonomyStore } = opts;

  if (originalTaxonomyVersion === targetTaxonomyVersion) {
    return originalCategoryId;
  }

  const targetTaxonomy = taxonomyStore.getVersion(targetTaxonomyVersion);
  if (!targetTaxonomy) return originalCategoryId;

  // Walk migration chain from originalVersion to targetVersion
  let currentId = originalCategoryId;
  let currentVersion = originalTaxonomyVersion;

  for (const migration of targetTaxonomy.migrations) {
    if (migration.fromVersion === currentVersion) {
      const remapped = migration.categoryRemap[currentId];
      if (remapped) {
        currentId = remapped;
        currentVersion = migration.toVersion;
      }
      if (currentVersion === targetTaxonomyVersion) break;
    }
  }

  return currentId;
}

/** Returns true if categoryId exists and is active in the given taxonomy */
export function isCategoryActive(categoryId: string, taxonomy: ExpertiseTaxonomy): boolean {
  return taxonomy.categories.some((c) => c.id === categoryId && c.status === 'active');
}

/** Returns the TaxonomyCategory for a given id, or undefined */
export function findCategory(
  categoryId: string,
  taxonomy: ExpertiseTaxonomy,
): TaxonomyCategory | undefined {
  return taxonomy.categories.find((c) => c.id === categoryId);
}

// ---------------------------------------------------------------------------
// Semver helpers (no external dependency)
// ---------------------------------------------------------------------------

function parseSemver(version: string): [number, number, number] {
  const parts = version.split('.').map(Number);
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
}

function compareSemver(a: string, b: string): number {
  const [aMaj, aMin, aPatch] = parseSemver(a);
  const [bMaj, bMin, bPatch] = parseSemver(b);
  return aMaj !== bMaj ? aMaj - bMaj : aMin !== bMin ? aMin - bMin : aPatch - bPatch;
}

function isNewerVersion(candidate: string, current: string): boolean {
  return compareSemver(candidate, current) > 0;
}
